import { gql } from "@apollo/client";
import type { DocumentNode } from "graphql";

/**
 * `/n3?mesureIds=...` resolution — Phase 9C.3B.5.
 *
 * There is no existing "resolve several mesures by id in one shot" query
 * in `packages/nbs-graphql/src/schema/typeDefs.ts` (its only list-shaped
 * query, `mesures(filter: { indicateurId, status }, first, after)`, is
 * filtered by indicateur/status, never by an arbitrary list of
 * `Mesure` ids — confirmed by `PHASE_9C3B0_TECHNICAL_AUDIT.md` §0.2 and by
 * re-reading `packages/nbs-graphql/src/schema/typeDefs.ts` during this
 * phase's audit). Per the phase's own STOP rule, no new backend
 * capability is invented for this: the existing, single-id
 * `mesure(id: ID!): Mesure` field (already used by
 * `features/review/api/documents.ts`'s `MESURE_PROVENANCE_QUERY`) is
 * reused as-is, aliased once per requested id inside a *single* GraphQL
 * document/HTTP request — never one request per row (§3.4 "ne pas
 * effectuer une requête par ligne si une résolution groupée existe" is
 * satisfied at the transport level: exactly one `useQuery` call, one
 * network round trip, regardless of how many ids were requested).
 *
 * No field is selected here beyond what the minimal target render needs
 * to confirm a resolved measure (`id`, `indicateurId`, `valeur`, `unite`,
 * `status`) — no `emprise`/`period`/`methode`/`qualificatif`/`version`/
 * `qualite`/`provenance`/`historique`/`auditEvents`, all of which exist on
 * the real `Mesure` SDL type but would start building the full scientific
 * N3 experience explicitly out of scope for this phase (§3.4 "Ne pas
 * construire la page scientifique N3 complète").
 */
const MESURE_MINIMAL_FIELDS = `
    id
    indicateurId
    valeur
    unite
    status
`;

/**
 * Builds the aliased multi-`mesure(id)` document for exactly the ids
 * given, in the exact order given (alias index == position in `ids`, so
 * resolving `data[\`m\${i}\`]` back to `ids[i]` is a direct, deterministic
 * lookup — see `useN3Mesures`).
 *
 * `ids` must be non-empty and already normalized (deduplicated, trimmed,
 * non-empty) by `parseMesureIdsParam` — this function performs no
 * normalization of its own; it is a pure GraphQL-document builder, not a
 * second parser. For an empty list it still returns a syntactically valid
 * document (a single `__typename` meta-field — a GraphQL introspection
 * field, not a business field) so a defensive call never throws; callers
 * (`useN3Mesures`) never actually execute it in that case (`skip: true`).
 */
export function buildMesuresByIdsQuery(ids: readonly string[]): DocumentNode {
  if (ids.length === 0) {
    return gql(`query MesuresByIdsEmpty { __typename }`);
  }

  const variableDefinitions = ids.map((_, index) => `$id${index}: ID!`).join(", ");
  const fields = ids
    .map((_, index) => `m${index}: mesure(id: $id${index}) {${MESURE_MINIMAL_FIELDS}  }`)
    .join("\n");

  return gql(`query MesuresByIds(${variableDefinitions}) {\n${fields}\n}`);
}

/** Variables companion to `buildMesuresByIdsQuery(ids)` — same order, same aliasing scheme. */
export function buildMesuresByIdsVariables(ids: readonly string[]): Record<string, string> {
  return Object.fromEntries(ids.map((id, index) => [`id${index}`, id]));
}
